import React, { useEffect, useMemo, useState } from 'react';
import { Table, Input, Space, Typography, Button, Select, Tag } from 'antd';
import api from '../utils/api';
import { useNavigate } from 'react-router-dom';

const { Title } = Typography;
const { Search } = Input;

const getToken = () => {
    const value = `; ${document.cookie}`;
    const parts = value.split('; token=');
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
};

const LeadLogs = () => {
    const [data, setData] = useState([]);
    // searchInput is what is typed; searchText is what has been submitted.
    const [searchInput, setSearchInput] = useState('');
    const [searchText, setSearchText] = useState('');
    const [productFilter, setProductFilter] = useState(null);
    const [contactedFilter, setContactedFilter] = useState(null);
    const [usernameFilter, setUsernameFilter] = useState(null);
    const [userEmailFilter, setUserEmailFilter] = useState(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const fetchData = async (params = {}) => {
        setLoading(true);
        try {
            const token = getToken();
            const response = await api.get('/lead-logs', {
                params,
                headers: { Authorization: `Bearer ${token}` },
            });
            setData(response.data || []);
        } catch (error) {
            console.error('Error fetching lead logs:', error);
            alert('Session expired. Redirecting to login.');
            navigate('/');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const productOptions = useMemo(() => {
        const items = Array.from(new Set(data.map((r) => r.product).filter(Boolean))).sort();
        return items.map((p) => ({ label: p, value: p }));
    }, [data]);

    // Account owner filters. The values live on the populated userID, so unlike
    // product/contacted they are resolved here from the loaded rows rather than
    // being pushed down to /lead-logs as query params.
    const usernameOptions = useMemo(() => {
        const items = Array.from(new Set(data.map((r) => r.username).filter(Boolean))).sort();
        return items.map((u) => ({ label: u, value: u }));
    }, [data]);

    const userEmailOptions = useMemo(() => {
        const items = Array.from(new Set(data.map((r) => r.userEmail).filter(Boolean))).sort();
        return items.map((e) => ({ label: e, value: e }));
    }, [data]);

    const applyServerFilters = () => {
        const params = {};
        if (productFilter) params.product = productFilter;
        if (contactedFilter !== null) params.contacted = String(contactedFilter);
        fetchData(params);
    };

    const filteredData = useMemo(() => {
        const q = searchText.trim().toLowerCase();
        return data.filter((row) => {
            if (usernameFilter && row.username !== usernameFilter) return false;
            if (userEmailFilter && row.userEmail !== userEmailFilter) return false;
            if (!q) return true;
            return (
                String(row.lead?.name || '').toLowerCase().includes(q) ||
                String(row.lead?.email || '').toLowerCase().includes(q) ||
                String(row.lead?.phone || '').toLowerCase().includes(q) ||
                String(row.subscriptionID || '').toLowerCase().includes(q) ||
                String(row.product || '').toLowerCase().includes(q) ||
                String(row.username || '').toLowerCase().includes(q) ||
                String(row.userEmail || '').toLowerCase().includes(q)
            );
        });
    }, [data, searchText, usernameFilter, userEmailFilter]);

    const columns = [
        { title: 'Product', dataIndex: 'product', key: 'product', width: 90 },
        { title: 'Lead ID', dataIndex: ['lead', 'id'], key: 'leadId', width: 280 },
        { title: 'Name', dataIndex: ['lead', 'name'], key: 'name', width: 180 },
        { title: 'Email', dataIndex: ['lead', 'email'], key: 'email', width: 230 },
        { title: 'Phone', dataIndex: ['lead', 'phone'], key: 'phone', render: (v) => v || 'N/A' },
        { title: 'Session', dataIndex: ['lead', 'session_id'], key: 'session', render: (v) => v || 'N/A' },
        {
            title: 'Lead DateTime',
            dataIndex: ['lead', 'datetime'],
            key: 'datetime',
            render: (d) => (d ? new Date(d).toLocaleString() : 'N/A'),
            width: 180,
        },
        {
            title: 'Contacted',
            dataIndex: ['lead', 'contacted'],
            key: 'contacted',
            render: (c) => <Tag color={c ? 'green' : 'default'}>{c ? 'Yes' : 'No'}</Tag>,
            width: 110,
        },
        {
            title: 'Source Version',
            dataIndex: ['lead', 'source_version'],
            key: 'sourceVersion',
            render: (v) => v || 'N/A',
            width: 130,
        },
        { title: 'Subscription ID', dataIndex: 'subscriptionID', key: 'subscriptionID', width: 240 },
        { title: 'Username', dataIndex: 'username', key: 'username', width: 150 },
        { title: 'User Email', dataIndex: 'userEmail', key: 'userEmail', width: 220 },
    ];

    return (
        <div style={{ padding: '20px' }}>
            <Title level={2}>Lead Logs</Title>
            <Button onClick={() => navigate('/dashboard')}>Dashboard</Button>
            <br />
            <Space wrap style={{ marginTop: 14, marginBottom: 20 }}>
                <Search
                    placeholder="Search name, email, phone, product, subscription, username, user email"
                    value={searchInput}
                    onSearch={setSearchText}
                    onChange={(e) => {
                        setSearchInput(e.target.value);
                        // Clearing the box restores the full list without needing Enter.
                        if (!e.target.value) setSearchText('');
                    }}
                    enterButton
                    allowClear
                    style={{ width: 360 }}
                />
                <Select
                    placeholder="Filter by product"
                    options={productOptions}
                    allowClear
                    value={productFilter}
                    onChange={setProductFilter}
                    style={{ width: 180 }}
                />
                <Select
                    placeholder="Filter by username"
                    options={usernameOptions}
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    value={usernameFilter}
                    onChange={(value) => setUsernameFilter(value ?? null)}
                    style={{ width: 200 }}
                />
                <Select
                    placeholder="Filter by user email"
                    options={userEmailOptions}
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    value={userEmailFilter}
                    onChange={(value) => setUserEmailFilter(value ?? null)}
                    style={{ width: 240 }}
                />
                <Select
                    placeholder="Filter by contacted"
                    allowClear
                    value={contactedFilter}
                    onChange={setContactedFilter}
                    style={{ width: 180 }}
                    options={[
                        { label: 'Contacted', value: true },
                        { label: 'Not Contacted', value: false },
                    ]}
                />
                <Button type="primary" onClick={applyServerFilters}>Apply Filters</Button>
                <Button onClick={() => {
                    setProductFilter(null);
                    setContactedFilter(null);
                    setUsernameFilter(null);
                    setUserEmailFilter(null);
                    setSearchInput('');
                    setSearchText('');
                    fetchData();
                }}>
                    Reset
                </Button>
            </Space>
            <Table
                columns={columns}
                dataSource={filteredData}
                rowKey="_id"
                loading={loading}
                scroll={{ x: 2200 }}
                pagination={{ pageSize: 20, showSizeChanger: true }}
            />
        </div>
    );
};

export default LeadLogs;
